import React, { useEffect, useMemo, useState } from "react";
import { Kanban, LifeBuoy, Plus, Table2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import SavOpenDialog from "../components/sav/SavOpenDialog";
import SavTable from "../components/sav/SavTable";
import SavKanban from "../components/sav/SavKanban";
import SavFiltersBar from "../components/sav/SavFiltersBar";
import SavViewsSidebar from "../components/sav/SavViewsSidebar";
import SavRelancesBar from "../components/sav/SavRelancesBar";
import { useSavOptions } from "../components/sav/SavFields";
import { allColumns, applyFilters, DEFAULT_COLUMNS, sortItems, systemViews, viewStatuses } from "../components/sav/savViews";

export default function SavListPage() {
  const opts = useSavOptions();
  const [items, setItems] = useState(null);
  const [custom, setCustom] = useState([]);
  const [view, setView] = useState(null);
  const [mode, setMode] = useState(() => localStorage.getItem("sav_mode") || "table");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/sav").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  const loadViews = () => api.get("/sav/views").then((r) => setCustom(r.data.items)).catch(() => {});
  useEffect(() => { load(); loadViews(); }, []);
  const system = useMemo(() => systemViews(opts?.subcontractors || []), [opts]);
  const [params] = useSearchParams();
  useEffect(() => { if (opts && !view) setView(system.find((v) => v.id === params.get("vue")) || system.find((v) => v.id === "all")); }, [opts]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = useMemo(() => allColumns([...(opts?.fields || []), ...(opts?.client_fields || [])]), [opts]);
  const counts = useMemo(() => Object.fromEntries([...system, ...custom].map((v) => [v.id, applyFilters(items || [], v.filters || {}).length])), [system, custom, items]);
  if (!opts || !view) return <AppLayout><div className="p-8 text-sm text-norea-textMuted">Chargement…</div></AppLayout>;
  const shown = sortItems(applyFilters(items || [], view.filters || {}), view.sort, cols);
  const viewCols = (view.columns?.length ? view.columns : DEFAULT_COLUMNS).map((id) => cols.find((c) => c.id === id)).filter(Boolean);
  const body = () => ({ name: view.name, filters: view.filters || {}, columns: view.columns, sort: view.sort || {} });
  const saveAs = async () => {
    const name = window.prompt("Nom de la vue (visible par toute l'équipe) :", view.system ? "" : view.name);
    if (!name?.trim()) return;
    try { const { data } = await api.post("/sav/views", { ...body(), name }); toast.success(`Vue « ${data.name} » enregistrée`); await loadViews(); setView(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };
  const update = async () => {
    try { const { data } = await api.put(`/sav/views/${view.id}`, body()); toast.success("Vue mise à jour"); await loadViews(); setView(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mise à jour impossible"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Supprimer la vue « ${v.name} » pour toute l'équipe ?`)) return;
    try { await api.delete(`/sav/views/${v.id}`); await loadViews(); if (view.id === v.id) setView(system[0]); } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const switchMode = (m) => { setMode(m); localStorage.setItem("sav_mode", m); };
  return (
    <AppLayout>
      <div className="flex gap-5" data-testid="sav-list-page">
        <SavViewsSidebar system={system} custom={custom} activeId={view.id} counts={counts} onPick={(v) => setView({ ...v, filters: { ...(v.filters || {}) } })} onDelete={remove} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-xs text-norea-textMuted">Service Après-Vente</div>
              <h1 className="font-heading text-3xl font-medium text-norea-text flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-norea-primary" /><span data-testid="sav-view-title">{view.name}</span>
                <span className="text-base text-norea-textMuted font-normal" data-testid="sav-view-total">· {shown.length}</span></h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white">
                {[["table", Table2, "Tableau"], ["kanban", Kanban, "Kanban"]].map(([k, Icon, l]) => (
                  <button key={k} onClick={() => switchMode(k)} className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1 transition-colors ${mode === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`sav-mode-${k}`}><Icon className="w-3.5 h-3.5" />{l}</button>
                ))}
              </div>
              <button onClick={() => setOpen(true)} className="btn-primary text-sm" data-testid="sav-new-btn"><Plus className="w-4 h-4" /> Nouvelle demande</button>
            </div>
          </div>
          <SavRelancesBar onRun={load} />
          <SavFiltersBar opts={opts} view={view} setView={setView} allCols={cols} onSaveAs={saveAs} onUpdate={update} />
          {items === null ? <div className="h-40 bg-white border border-norea-border rounded-md animate-pulse" />
            : mode === "kanban" ? <SavKanban items={shown} statuses={viewStatuses(opts, view.filters)} onMoved={load} />
              : <SavTable items={shown} cols={viewCols} sort={view.sort} onSort={(sort) => setView({ ...view, sort })} />}
        </div>
      </div>
      {open && <SavOpenDialog onClose={() => setOpen(false)} />}
    </AppLayout>
  );
}
